import { useEffect, useState } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Mail, Phone, Copy, AlertTriangle, KeyRound } from "lucide-react";
import { useT } from "@/lib/i18n";

const TwoFactorSettings = () => {
  const t = useT();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  // setup flow state
  const [setupChannel, setSetupChannel] = useState("email");
  const [setupChallenge, setSetupChallenge] = useState(null);
  const [setupCode, setSetupCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  // disable flow state
  const [disablePwd, setDisablePwd] = useState("");
  const [showDisable, setShowDisable] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup", { channel: setupChannel });
      setSetupChallenge(data);
      toast.success(`${t("twofa.settings.toast_code_sent")} ${data.destination_masked}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("twofa.settings.toast_setup_failed"));
    } finally { setBusy(false); }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", {
        challenge_token: setupChallenge.challenge_token,
        code: setupCode.trim(),
      });
      if (data.recovery_codes) setRecoveryCodes(data.recovery_codes);
      setSetupChallenge(null);
      setSetupCode("");
      toast.success(t("twofa.settings.toast_enabled"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("twofa.toast_invalid"));
    } finally { setBusy(false); }
  };

  const disable2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePwd });
      toast.success(t("twofa.settings.toast_disabled"));
      setShowDisable(false);
      setDisablePwd("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("twofa.settings.toast_disable_failed"));
    } finally { setBusy(false); }
  };

  const copyCodes = () => {
    if (!recoveryCodes) return;
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success(t("twofa.recovery.toast_copied"));
  };

  if (!status) return <div className="rn3-card text-sm text-[#737373]" data-testid="twofa-loading">{t("twofa.settings.loading")}</div>;

  // After setup, surface the recovery codes ONCE.
  if (recoveryCodes) {
    return (
      <div className="rn3-card border-[#C4A252]" data-testid="twofa-recovery-codes">
        <div className="rn3-eyebrow flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#C4A252]" /> {t("twofa.recovery.eyebrow")}</div>
        <h3 className="font-serif-display text-2xl mt-2">{t("twofa.recovery.title")}</h3>
        <div className="border-t border-[#C4A252] w-10 mt-3 mb-4" />
        <p className="text-sm text-[#404040]">
          {t("twofa.recovery.body_a")} <b> {t("twofa.recovery.body_b")}</b> {t("twofa.recovery.body_c")}
        </p>
        <div className="grid grid-cols-2 gap-2 mt-5 font-mono text-sm" data-testid="recovery-codes-list">
          {recoveryCodes.map((c, i) => (
            <div key={i} className="border border-[#E5E5E5] bg-[#FDFBF7] py-2 px-3 text-center">{c}</div>
          ))}
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={copyCodes} className="rn3-btn-gold" data-testid="copy-codes">
            <Copy className="w-4 h-4" /> {t("twofa.recovery.copy_all")}
          </button>
          <button onClick={() => setRecoveryCodes(null)} className="rn3-btn-primary" data-testid="recovery-done">
            {t("twofa.recovery.done")}
          </button>
        </div>
      </div>
    );
  }

  // Mid-setup: enter code
  if (setupChallenge) {
    return (
      <form onSubmit={confirmSetup} className="rn3-card" data-testid="twofa-setup-verify">
        <div className="rn3-eyebrow">{t("twofa.settings.confirm_setup_eyebrow")}</div>
        <h3 className="font-serif-display text-2xl mt-2">{t("twofa.settings.confirm_setup_title")}</h3>
        <div className="border-t border-[#C4A252] w-10 mt-3 mb-4" />
        <p className="text-sm text-[#404040]">{t("twofa.settings.confirm_sent_to")} <b>{setupChallenge.destination_masked}</b>.</p>
        <input
          required
          autoFocus
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={setupCode}
          onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ""))}
          className="rn3-input text-center tracking-[0.5em] font-mono text-2xl mt-4"
          placeholder="••••••"
          data-testid="setup-code-input"
        />
        <div className="mt-5 flex gap-3">
          <button type="submit" disabled={busy} className="rn3-btn-primary" data-testid="setup-confirm">
            {busy ? "…" : t("twofa.settings.confirm_btn")}
          </button>
          <button type="button" onClick={() => { setSetupChallenge(null); setSetupCode(""); }} className="text-sm text-[#737373] underline">{t("twofa.cancel")}</button>
        </div>
      </form>
    );
  }

  return (
    <div className="rn3-card" data-testid="twofa-settings">
      <div className="flex items-center justify-between">
        <div>
          <div className="rn3-eyebrow flex items-center gap-2">
            {status.enabled ? <ShieldCheck className="w-4 h-4 text-green-600" /> : <ShieldOff className="w-4 h-4 text-[#737373]" />}
            {t("twofa.settings.eyebrow")}
          </div>
          <h3 className="font-serif-display text-2xl mt-2">
            {status.enabled ? t("twofa.settings.enabled") : t("twofa.settings.not_enabled")}
          </h3>
          {status.enabled && (
            <div className="text-xs text-[#737373] mt-1">
              {t("twofa.settings.channel_label")} <b className="uppercase">{status.channel}</b> · {t("twofa.settings.recovery_remaining")} <b>{status.recovery_codes_remaining}</b>
            </div>
          )}
          {status.required && !status.enabled && (
            <div className="mt-2 text-xs text-[#8B1C22] flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {t("twofa.settings.required_warning")}
            </div>
          )}
        </div>
      </div>
      <div className="border-t border-[#C4A252] w-10 mt-3 mb-5" />

      {!status.enabled && (
        <div className="space-y-4" data-testid="twofa-enable-section">
          <p className="text-sm text-[#404040]">
            {t("twofa.settings.intro")}
          </p>
          <div>
            <label className="rn3-eyebrow block mb-2">{t("twofa.settings.choose_channel")}</label>
            <div className="flex gap-2" data-testid="channel-picker">
              <button
                type="button"
                onClick={() => setSetupChannel("email")}
                className={`flex-1 py-3 border text-sm transition-colors flex items-center justify-center gap-2 ${setupChannel === "email" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:border-[#C4A252]"}`}
                data-testid="channel-email"
              >
                <Mail className="w-4 h-4" /> {t("twofa.settings.email")}
              </button>
              <button
                type="button"
                onClick={() => setSetupChannel("sms")}
                disabled={!status.phone_on_file}
                className={`flex-1 py-3 border text-sm transition-colors flex items-center justify-center gap-2 ${setupChannel === "sms" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:border-[#C4A252]"} disabled:opacity-40 disabled:cursor-not-allowed`}
                data-testid="channel-sms"
              >
                <Phone className="w-4 h-4" /> {t("twofa.settings.sms")} {status.phone_on_file ? "" : t("twofa.settings.sms_disabled_hint")}
              </button>
            </div>
          </div>
          <button onClick={startSetup} disabled={busy} className="rn3-btn-primary" data-testid="enable-2fa-btn">
            <ShieldCheck className="w-4 h-4" /> {busy ? t("twofa.settings.sending") : t("twofa.settings.send_setup_code")}
          </button>
        </div>
      )}

      {status.enabled && !status.required && (
        <div data-testid="twofa-disable-section">
          {!showDisable ? (
            <button onClick={() => setShowDisable(true)} className="text-sm text-[#8B1C22] underline" data-testid="disable-2fa-link">
              {t("twofa.settings.disable_link")}
            </button>
          ) : (
            <form onSubmit={disable2fa} className="space-y-3">
              <p className="text-xs text-[#737373]">{t("twofa.settings.disable_hint")}</p>
              <input
                required
                type="password"
                value={disablePwd}
                onChange={(e) => setDisablePwd(e.target.value)}
                className="rn3-input"
                placeholder={t("twofa.settings.your_password")}
                data-testid="disable-password"
              />
              <div className="flex gap-2">
                <button type="submit" disabled={busy} className="rn3-btn-primary" data-testid="disable-confirm">
                  {busy ? "…" : t("twofa.settings.disable_btn")}
                </button>
                <button type="button" onClick={() => { setShowDisable(false); setDisablePwd(""); }} className="text-sm text-[#737373] underline">{t("twofa.cancel")}</button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default TwoFactorSettings;
